import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Image as ImageIcon } from 'lucide-react';
import { industrialProductsAPI, supplierProductsAPI } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Picker showing all industrial + supplier products that have an image.
 * onPick({ sourceType: 'industrial' | 'supplier', sourceId, imageUrl, label })
 */
export default function ImagePickerDialog({ open, onClose, onPick, excludeId, excludeType, currentIndustrialProductId = null }) {
    const [industrial, setIndustrial] = useState([]);
    const [supplier, setSupplier] = useState([]);
    const [search, setSearch] = useState('');
    const [tab, setTab] = useState('all'); // 'all' | 'industrial' | 'supplier'
    const [loading, setLoading] = useState(false);
    const BACKEND = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [ipRes, spRes] = await Promise.all([
                    industrialProductsAPI.getAll(),
                    supplierProductsAPI.getAll(),
                ]);
                if (cancelled) return;
                setIndustrial((ipRes.data || []).filter(p => p.image_url));
                setSupplier((spRes.data || []).filter(p => p.image_url));
            } catch (e) {
                toast.error("Erreur lors du chargement des images");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open]);

    // Build a map id -> industrial product (including those without an image,
    // because we want to enrich SPs' searchable text with their parent name
    // even if the parent has no image of its own).
    const [allIndustrial, setAllIndustrial] = useState([]);
    useEffect(() => {
        if (!open) return;
        industrialProductsAPI.getAll()
            .then(r => setAllIndustrial(r.data || []))
            .catch(() => setAllIndustrial([]));
    }, [open]);
    const ipById = useMemo(() => {
        const m = new Map();
        for (const p of allIndustrial) m.set(p.id, p);
        return m;
    }, [allIndustrial]);

    const normalize = (s) => (s || '').toString().toLowerCase();
    const q = normalize(search);

    const industrialFiltered = useMemo(() => industrial
        .filter(p => !(excludeType === 'industrial' && p.id === excludeId))
        .filter(p => !q || normalize(p.name).includes(q) || normalize(p.category).includes(q) || normalize(p.famille).includes(q))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr')),
    [industrial, q, excludeId, excludeType]);

    const supplierFiltered = useMemo(() => supplier
        .filter(p => !(excludeType === 'supplier' && p.id === excludeId))
        .filter(p => {
            if (!q) return true;
            const ip = ipById.get(p.industrial_product_id);
            const ipName = normalize(ip?.name);
            return normalize(p.supplier_name_product).includes(q)
                || normalize(p.brand).includes(q)
                || normalize(p.supplier_ref).includes(q)
                || (ipName && ipName.includes(q));
        })
        .sort((a, b) => (a.supplier_name_product || '').localeCompare(b.supplier_name_product || '', 'fr')),
    [supplier, q, excludeId, excludeType, ipById]);

    // Supplier products that share the same industrial parent as the currently
    // edited supplier product — surfaced as a dedicated "same product" section.
    const sameProductSupplier = useMemo(() => {
        if (!currentIndustrialProductId) return [];
        return supplierFiltered.filter(p => p.industrial_product_id === currentIndustrialProductId);
    }, [supplierFiltered, currentIndustrialProductId]);
    const otherSupplier = useMemo(
        () => supplierFiltered.filter(p => !sameProductSupplier.includes(p)),
        [supplierFiltered, sameProductSupplier]
    );

    const handlePick = (sourceType, p) => {
        const label = sourceType === 'industrial' ? p.name : (p.supplier_name_product || p.supplier_ref);
        onPick({
            sourceType,
            sourceId: p.id,
            imageUrl: `${BACKEND}${p.image_url}`,
            label,
        });
    };

    const renderGrid = (items, sourceType) => (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {items.map(p => {
                const label = sourceType === 'industrial' ? p.name : (p.supplier_name_product || p.supplier_ref);
                return (
                    <button
                        key={`${sourceType}-${p.id}`}
                        type="button"
                        onClick={() => handlePick(sourceType, p)}
                        className="group relative rounded-lg border border-slate-200 hover:border-emerald-400 hover:ring-2 hover:ring-emerald-200 overflow-hidden bg-white transition"
                        data-testid={`image-picker-${sourceType}-${p.id}`}
                        title={label}
                    >
                        <div className="aspect-square bg-slate-50 flex items-center justify-center">
                            <img
                                src={`${BACKEND}${p.image_url}`}
                                alt={label}
                                loading="lazy"
                                className="w-full h-full object-contain"
                            />
                        </div>
                        <div className="px-1.5 py-1 text-[11px] font-medium text-slate-700 truncate bg-white border-t border-slate-100">
                            {label}
                        </div>
                    </button>
                );
            })}
            {items.length === 0 && (
                <div className="col-span-full text-center text-sm text-slate-400 py-8">
                    Aucun résultat
                </div>
            )}
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="image-picker-dialog">
                <DialogHeader>
                    <DialogTitle>Choisir une photo existante</DialogTitle>
                    <DialogDescription>
                        Sélectionnez la photo d'un produit industriel ou fournisseur déjà présent.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <Input
                            placeholder="Rechercher un produit..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-8 h-9"
                            data-testid="image-picker-search"
                        />
                    </div>
                    <div className="flex items-center gap-1">
                        {[
                            { key: 'all', label: `Tous (${industrialFiltered.length + supplierFiltered.length})` },
                            { key: 'industrial', label: `Industriels (${industrialFiltered.length})` },
                            { key: 'supplier', label: `Fournisseurs (${supplierFiltered.length})` },
                        ].map(t => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setTab(t.key)}
                                className={`px-3 h-9 rounded-md text-xs font-semibold border transition-colors ${
                                    tab === t.key
                                        ? 'bg-slate-900 text-white border-slate-900'
                                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                }`}
                                data-testid={`image-picker-tab-${t.key}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-auto mt-3 pr-1">
                    {loading ? (
                        <div className="flex items-center justify-center py-10 text-slate-400 text-sm">
                            <ImageIcon className="w-5 h-5 mr-2" /> Chargement des images...
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {(tab === 'all' || tab === 'supplier') && sameProductSupplier.length > 0 && (
                                <div data-testid="image-picker-same-product-section">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">
                                        Autres fournisseurs du même produit ({sameProductSupplier.length})
                                    </h3>
                                    {renderGrid(sameProductSupplier, 'supplier')}
                                </div>
                            )}
                            {(tab === 'all' || tab === 'industrial') && (
                                <div>
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                                        Produits industriels
                                    </h3>
                                    {renderGrid(industrialFiltered, 'industrial')}
                                </div>
                            )}
                            {(tab === 'all' || tab === 'supplier') && (
                                <div>
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                                        Produits fournisseurs{sameProductSupplier.length > 0 ? ' (autres)' : ''}
                                    </h3>
                                    {renderGrid(otherSupplier, 'supplier')}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex justify-end pt-3 border-t">
                    <Button variant="outline" onClick={onClose} data-testid="image-picker-cancel">
                        Annuler
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
